import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { FORGOT_PASSWORD } from '@/constants/testIds';
import { MapPin, ArrowLeft, Mail, CheckCircle2, Loader2 } from 'lucide-react';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await api.post('/auth/forgot-password', { email: email.trim() });
            setSent(true);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Something went wrong. Please try again in a moment.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--ns-bg)] flex" data-testid={FORGOT_PASSWORD.page}>
            <div className="hidden lg:flex flex-1 relative overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/3822155/pexels-photo-3822155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                    alt="Travel"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(29,45,68,0.55), rgba(224,122,95,0.35))' }} />
                <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
                    <div className="flex items-center gap-2">
                        <span className="w-10 h-10 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur">
                            <MapPin size={18} />
                        </span>
                        <span className="font-display text-2xl font-semibold">NextStop</span>
                    </div>
                    <div className="max-w-md">
                        <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight tracking-tight">
                            No stress.<br />Happens to everyone.
                        </h1>
                        <p className="mt-4 text-white/80 text-lg leading-relaxed">
                            Pop your email in and we&rsquo;ll send a link to reset it. Link is good for one hour.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'var(--ns-navy)' }}>
                            <MapPin size={18} className="text-white" />
                        </span>
                        <span className="font-display text-2xl font-semibold">NextStop</span>
                    </div>

                    <Link
                        to="/login"
                        data-testid={FORGOT_PASSWORD.backToLogin}
                        className="inline-flex items-center gap-1.5 text-sm text-[var(--ns-text2)] hover:text-[var(--ns-text)] mb-6"
                    >
                        <ArrowLeft size={14} /> Back to sign in
                    </Link>

                    <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Reset your password</h2>
                    <p className="text-[var(--ns-text2)] mt-2">
                        Enter the email you use for NextStop and we&rsquo;ll send you a link to set a new password.
                    </p>

                    {sent ? (
                        <div
                            data-testid={FORGOT_PASSWORD.confirmationMessage}
                            className="mt-8 rounded-2xl border border-[var(--ns-border)] bg-white p-6"
                        >
                            <div className="flex items-start gap-3">
                                <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(63,142,91,0.14)' }}>
                                    <CheckCircle2 size={16} style={{ color: 'var(--ns-success,#3F8E5B)' }} />
                                </span>
                                <div>
                                    <div className="font-display text-lg font-semibold tracking-tight">Check your inbox</div>
                                    <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">
                                        If an account exists for <strong>{email.trim() || 'that email'}</strong>, a reset
                                        link is on its way. It&rsquo;s good for the next hour.
                                    </p>
                                    <p className="text-xs text-[var(--ns-text3)] mt-3 leading-relaxed">
                                        Nothing arrive? Check spam, then try again — or reach out at support@nextstoponline.com.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-5 flex items-center gap-3">
                                <Link to="/login">
                                    <Button variant="outline" className="rounded-full h-10 px-4 border-[var(--ns-border)]">Return to sign in</Button>
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => { setSent(false); }}
                                    className="text-sm text-[var(--ns-text2)] hover:text-[var(--ns-clay)]"
                                >
                                    Use a different email
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-8 space-y-4">
                            <div>
                                <Label htmlFor="email" className="text-sm text-[var(--ns-text2)]">Email</Label>
                                <div className="relative mt-1">
                                    <Mail
                                        size={14}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ns-text3)]"
                                    />
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        data-testid={FORGOT_PASSWORD.emailInput}
                                        className="h-12 rounded-xl border-[var(--ns-border)] bg-white pl-10"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div
                                    data-testid={FORGOT_PASSWORD.errorAlert}
                                    className="text-sm text-[var(--ns-clayHover)] bg-[var(--ns-clay)]/10 border border-[var(--ns-clay)]/30 rounded-xl px-4 py-3"
                                >
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid={FORGOT_PASSWORD.submitButton}
                                className="w-full h-12 rounded-full font-medium text-white"
                                style={{ background: 'var(--ns-navy)' }}
                            >
                                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending link…</> : 'Send reset link'}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
